import Vue from "vue"
import Router from "vue-router"
import store from "../store"

Vue.use(Router)

// New Created Function for Redirected Not allowed users from pages (eg: student want to visit /tutor-catalogs), redirect him to "/"
const authorizeByRoles = (to, from, next) => {
  if (!store.getters.isAuthenticated) {
    next("/")
    return
  }

  const userType = store.getters.StateUser.user?.user_type || ""
  const allowedRoles = to.meta?.allowed || []

  const hasAccess = allowedRoles.some(role => userType.includes(role))

  if (allowedRoles.length > 0 && !hasAccess) {
    next("/")
  } else {
    next()
  }
}

const checkOperator = (to, from, next) => {
  // Check if this is the OperatorLogin route itself
  const isOperatorLoginRoute = to.name === 'OperatorLogin' || to.path === '/operatorlogin'
  
  const href = window.location.href.toLowerCase()
  // Remove trailing slashes and query parameters for comparison
  const cleanHref = href.split('?')[0].replace(/\/$/, '')
  const dev = process.env.VUE_APP_FRONTEND_URL ? (process.env.VUE_APP_FRONTEND_URL + "/operatorlogin").toLowerCase().replace(/\/$/, '') : ''
  const qc = process.env.VUE_APP_QC_DOMAIN ? (process.env.VUE_APP_QC_DOMAIN + "/operatorlogin").toLowerCase().replace(/\/$/, '') : ''
  const pre_prod = process.env.VUE_APP_PREPROD_DOMAIN ? (process.env.VUE_APP_PREPROD_DOMAIN + "/operatorlogin").toLowerCase().replace(/\/$/, '') : ''
  const website = process.env.VUE_APP_PROD_DOMAIN ? (process.env.VUE_APP_PROD_DOMAIN + "/operatorlogin").toLowerCase().replace(/\/$/, '') : ''
  
  // Check if current URL matches any operator login URL or if we're on the operator login route
  const isOperatorLoginUrl = isOperatorLoginRoute || cleanHref === website || cleanHref === qc || cleanHref === pre_prod || cleanHref === dev || cleanHref.includes('/operatorlogin')
  
  if (isOperatorLoginUrl) {
    // If user is already authenticated and trying to access operator login page, redirect to dashboard
    if (store.getters.isAuthenticated) {
      const userType = store.getters.StateUser?.user?.user_type
      if (userType === "operator" || userType === "institute_admin") {
        next({ name: "ApprovedTutors", query: { ins: 1 } })
        return
      } else {
        next("/")
        return
      }
    }
    
    // If route requires auth but user is not authenticated, open login modal
    if (to.matched.some(record => record.meta.requiresAuth)) {
      window.dispatchEvent(
        new CustomEvent("loginModalChange", {
          detail: {
            opened: true,
            tab: 0,
          },
        })
      )
      next(false) // Prevent navigation, modal will handle it
      return
    } 
    // If route is guest route (like OperatorLogin itself), allow navigation
    else if (to.matched.some(record => record.meta.guest)) {
      if (!store.getters.isAuthenticated) {
        next()
        return
      }
      // If authenticated, redirect based on user type
      const userType = store.getters.StateUser?.user?.user_type
      if (userType === "operator" || userType === "institute_admin") {
        next({ name: "ApprovedTutors", query: { ins: 1 } })
      } else {
        next("/")
      }
      return
    } else {
      next()
    }
  } else {
    // Not an operator login URL, proceed normally
    next()
  }
}

const checkInstituteAccess = (to, from, next) => {
  const institute = JSON.parse(localStorage.getItem("institute_configs"))
  if (institute && institute.id === 1) {
    next()
  } else {
    next("/")
  }
}

// Permission check for Student 360 View
// - Operator/Admin/Institute Admin: can view any student in their institute
// - Parent: can view their linked children
// - Student: can only view themselves
const checkStudent360Access = (to, from, next) => {
  if (!store.getters.isAuthenticated) {
    window.dispatchEvent(
      new CustomEvent("loginModalChange", {
        detail: {
          opened: true,
          tab: 0,
        },
      })
    )
    next(false)
    return
  }

  const userType = store.getters.StateUser?.user?.user_type || ""
  const studentId = to.params.id || to.params.studentId
  const currentUserId = store.getters.StateUser?.user?.id

  // Operator/Admin/Institute Admin: allow (detailed check will be done in component via API)
  if (
    userType.includes("operator") ||
    userType.includes("super_user") ||
    userType.includes("institute_admin")
  ) {
    next()
    return
  }

  // Parent: allow (detailed check will be done in component via API)
  // TODO: Add parent-child relationship check if needed
  if (userType.includes("parent")) {
    next()
    return
  }

  // Student: can only view themselves
  if (userType.includes("student") && !userType.includes("kid")) {
    if (parseInt(studentId) === currentUserId) {
      next()
      return
    } else {
      next("/")
      return
    }
  }

  // Tutor: allow (detailed check will be done in component via API)
  if (userType.includes("tutor")) {
    next()
    return
  }

  // Default: deny access
  next("/")
}

const checkLogin = (to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (store.getters.isAuthenticated) {
      if (
        to.matched.some(
          record =>
            record.meta.NotSocial == true &&
            store.getters.StateUser.user.user_type.indexOf("_") != -1
        )
      ) {
        next("/")
        return
      }
      if (
        to.matched.some(record => record.meta.layout == "global") ||
        to.matched.some(
          record => record.meta.layout == store.getters.StateUser.user.category
        )
      ) {
        next()
        return
      } else {
        next("/")
        return
      }
    }
    window.dispatchEvent(
      new CustomEvent("loginModalChange", {
        detail: {
          opened: true,
          tab: 0,
        },
      })
    )
    return
  } else if (to.matched.some(record => record.meta.guest)) {
    if (!store.getters.isAuthenticated) {
      next()
      return
    }
    next("/")
  } else {
    next()
  }
}

export default new Router({
  linkActiveClass: "active",
  scrollBehavior(to, from, savedPosition) {
    return { x: 0, y: 0, behavior: "smooth" }
  },
  routes: [
    {
      path: "/",
      name: "FancyHome",
      component: () =>
        import(/* webpackChunkName: "fancyHome" */ "../views/fancyHome.vue"),
      // meta: { layout: "global" },
      beforeEnter: checkLogin,
    },
    {
      path: "/tutors",
      name: "Tutors",
      component: () =>
        import(
          /* webpackChunkName: "TutorsView" */ "../views/NewReservation/TutorsView.vue"
        ),
      meta: { layout: "global" },
    },
    // for coming release (courses list and course details page) UNCOMMENT
    // {
    //   path: "/courses",
    //   name: "CoursesList",
    //   component: () =>
    //     import(
    //       /* webpackChunkName: "CoursesListView" */ "../views/KidsReservation/CoursesListView.vue"
    //     ),
    //   meta: { layout: "global" },
    // },
    // {
    //   path: "/courses/:id",
    //   name: "CourseTypeDetails",
    //   // route level code-splitting
    //   // this generates a separate chunk (about.[hash].js) for this route
    //   // which is lazy-loaded when the route is visited.
    //   component: () =>
    //     import(
    //       /* webpackChunkName: "about" */ "../views/CourseTypes/CourseTypeDetails.vue"
    //     ),
    // },
    {
      path: "/groups",
      name: "KidsCoursesList",
      component: () =>
        import(
          /* webpackChunkName: "KidsCoursesListView" */ "../views/KidsReservation/KidsCoursesListView.vue"
        ),
      meta: { layout: "global" },
      children: [
        {
          path: "programs",
          name: "KidsProgramsList",
          component: () =>
            import(
              /* webpackChunkName: "KidsProgramsList" */ "../views/KidsReservation/KidsProgramsListView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
          beforeEnter(to, from, next) {
            if (!store.getters.isAuthenticated)
              return window.dispatchEvent(
                new CustomEvent("loginModalChange", {
                  detail: {
                    opened: true,
                    tab: 0,
                  },
                })
              )
            if (!store.getters.GET_SELECTED_KIDS_COURSES.length) {
              store.dispatch("clearAllKidsSelections")
              return next({ name: "KidsCoursesList" })
            }
            next()
          },
        },
        {
          path: "kids",
          name: "KidsCourses",
          component: () =>
            import(
              /* webpackChunkName: "KidsCoursesView" */ "../views/KidsReservation/KidsCoursesView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
          beforeEnter(to, from, next) {
            if (!store.getters.isAuthenticated)
              return window.dispatchEvent(
                new CustomEvent("loginModalChange", {
                  detail: {
                    opened: true,
                    tab: 0,
                  },
                })
              )
            if (!store.getters.GET_SELECTED_KIDS_COURSES.length) {
              store.dispatch("clearAllKidsSelections")
              return next({ name: "KidsCoursesList" })
            }
            next()
          },
        },
        {
          path: "interview",
          name: "ScheduleInterview",
          component: () =>
            import(
              /* webpackChunkName: "ScheduleInterviewView" */ "../views/KidsReservation/ScheduleInterviewView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
          beforeEnter(to, from, next) {
            if (!store.getters.isAuthenticated)
              return window.dispatchEvent(
                new CustomEvent("loginModalChange", {
                  detail: {
                    opened: true,
                    tab: 0,
                  },
                })
              )
            if (!store.getters.GET_SELECTED_MATRIX.length) {
              store.dispatch("clearAllKidsSelections")
              return next({ name: "KidsCoursesList" })
            }
            next()
          },
        },
        {
          path: "reservation",
          name: "KidsCheckout",
          component: () =>
            import(
              /* webpackChunkName: "KidsCheckout" */ "../views/KidsReservation/KidsCheckoutView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
          beforeEnter(to, from, next) {
            if (!store.getters.isAuthenticated)
              return window.dispatchEvent(
                new CustomEvent("loginModalChange", {
                  detail: {
                    opened: true,
                    tab: 0,
                  },
                })
              )
            if (!store.getters.GET_WEBINAR_ID) {
              return next({ name: "ScheduleInterview" })
            }
            next()
          },
        },
      ],
    },
    {
      path: "/reservation/:id",
      redirect: "/reservation/:id/courses",
      name: "Reservation",
      component: () =>
        import(
          /* webpackChunkName: "ReservationView" */ "../views/NewReservation/ReservationView.vue"
        ),
      meta: { layout: "global", requiresAuth: true },
      children: [
        {
          name: "Courses",
          path: "courses",
          component: () =>
            import(
              /* webpackChunkName: "CoursesView" */ "../views/NewReservation/CoursesView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
        },
        {
          name: "ProgramsList",
          path: "programs",
          component: () =>
            import(
              /* webpackChunkName: "ProgramsView" */ "../views/NewReservation/ProgramsView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
          beforeEnter(to, from, next) {
            if (!store.getters.isAuthenticated)
              window.dispatchEvent(
                new CustomEvent("loginModalChange", {
                  detail: {
                    opened: true,
                    tab: 0,
                  },
                })
              )
            if (!store.getters.GET_SELECTED_COURSES.length) {
              store.dispatch("resetSelections")
              return next({ name: "Tutors" })
            }
            next()
          },
        },
        {
          name: "SessionsList",
          path: "sessions",
          component: () =>
            import(
              /* webpackChunkName: "SessionsView" */ "../views/NewReservation/SessionsView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
          beforeEnter(to, from, next) {
            if (!store.getters.isAuthenticated)
              return window.dispatchEvent(
                new CustomEvent("loginModalChange", {
                  detail: {
                    opened: true,
                    tab: 0,
                  },
                })
              )
            if (!store.getters.GET_SELECTED_PROGRAM) {
              store.dispatch("resetSelections")
              return next({ name: "Tutors" })
            }
            next()
          },
        },
        {
          name: "Payment",
          path: "checkout",
          component: () =>
            import(
              /* webpackChunkName: "PaymentView" */ "../views/NewReservation/PaymentView.vue"
            ),
          meta: { layout: "global", requiresAuth: true },
          beforeEnter(to, from, next) {
            if (!store.getters.isAuthenticated)
              return window.dispatchEvent(
                new CustomEvent("loginModalChange", {
                  detail: {
                    opened: true,
                    tab: 0,
                  },
                })
              )
            if (!store.getters.GET_SELECTED_SLOTS.length) {
              store.dispatch("resetSelections")
              return next({ name: "Tutors" })
            }
            next()
          },
        },
      ],
    },
    {
      path: "/TutorEvents",
      name: "TutorEvents",
      component: () =>
        import(
          /* webpackChunkName: "TutorEvents" */ "../views/TutorEvents.vue"
        ),
      meta: { layout: "global" },
      beforeEnter: checkLogin,
    },
    {
      path: "/EventSample",
      name: "EventSample",
      component: () =>
        import(
          /* webpackChunkName: "EventSample" */ "../views/EventSample.vue"
        ),
      meta: { layout: "global" },
      beforeEnter: checkLogin,
    },
    {
      path: "/operatorlogin",
      name: "OperatorLogin",
      component: () =>
        import(
          /* webpackChunkName: "OperatorLogin" */ "../views/OperatorLogin.vue"
        ),
      meta: { layout: "anonymous", guest: true },
      beforeEnter: checkOperator,
    },
    {
      path: "/institute-login",
      name: "instituteAdminLogin",
      component: () =>
        import(
          /* webpackChunkName: "instituteAdminLogin" */ "../views/instituteAdminLogin.vue"
        ),
      meta: { layout: "anonymous", guest: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/ForgetPassword",
      name: "ForgetPassword",
      component: () =>
        import(
          /* webpackChunkName: "ForgetPassword" */ "../views/ForgetPassword.vue"
        ),
      meta: { layout: "anonymous", guest: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/UpdatePassword",
      name: "UpdatePassword",
      component: () =>
        import(
          /* webpackChunkName: "UpdatePassword" */ "../views/UpdatePassword.vue"
        ),
      meta: { layout: "global", requiresAuth: true, NotSocial: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/Profile",
      name: "Profile",
      component: () =>
        import(/* webpackChunkName: "Profile" */ "../views/Profile.vue"),
      meta: { layout: "global", requiresAuth: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/meeting",
      name: "meeting",
      component: () =>
        import(/* webpackChunkName: "Meeting" */ "../views/Meeting.vue"),
      meta: { layout: "global" },
      beforeEnter: checkLogin,
    },
    {
      path: "/Webinars",
      name: "Webinars",
      component: () =>
        import(/* webpackChunkName: "Webinars" */ "../views/Webinars.vue"),
      // meta: { guest: true },
      // component: Webinars,
      meta: { layout: "global", guest: true },
      // beforeEnter: checkLogin
    },
    {
      path: "/StLoginforWebinar/:id?",
      name: "StLoginforWebinar",
      component: () =>
        import(
          /* webpackChunkName: "StLoginforWebinar" */ "../components/Login/StLoginforWebinar.vue"
        ),
      meta: { layout: "anonymous", guest: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/SubAccounts",
      name: "SubAccounts",
      component: () =>
        import(
          /* webpackChunkName: "SubAccounts" */ "../views/SubAccounts.vue"
        ),
      meta: { layout: "global", requiresAuth: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/dashboard",
      name: "StudentClasses",
      component: () =>
        import(
          /* webpackChunkName: "StudentClasses" */ "../views/StudentClasses.vue"
        ),
      meta: { layout: "global", requiresAuth: true },
      children: [
        {
          name: "StudentAnalyticsDashboard",
          path: "analytics",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentAnalyticsDashboard" */ "../views/StudentAnalyticsDashboard.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "CustomerPipeline",
          path: "customer-pipeline",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "CustomerPipeline" */ "../views/CustomerPipeline.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "BookedClasses",
          path: "BookedClasses",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "BookedClasses" */ "../components/StudenClasses/BookedClasses.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true },
        },
        {
          name: "BookedWebinars",
          path: "BookedWebinars",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "BookedWebinars" */ "../components/StudenClasses/BookedWebinars.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true },
        },
        {
          path: "StudentArchivedClasses",
          name: "StudentArchivedClasses",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentArchivedClasses" */ "../components/StudenClasses/StudentArchivedClasses.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true },
          beforeEnter: checkLogin,
        },
        {
          path: "class-status-approvals",
          name: "ClassStatusApprovals",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "ClassStatusApprovals" */ "../components/Dashboard/Classes/ClassStatusApprovals.vue"
              ),
          },
          meta: {
            layout: "global",
            requiresAuth: true,
            allowed: ["operator", "institute_admin", "super_user"],
          },
          beforeEnter: authorizeByRoles,
        },
        {
          path: "dashboard-approvals",
          name: "DashboardApprovals",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "DashboardApprovals" */ "../components/Dashboard/Classes/approvals/index.vue"
              ),
          },
          meta: {
            layout: "global",
            requiresAuth: true,
            allowed: ["operator", "institute_admin", "super_user"],
          },
          beforeEnter: authorizeByRoles,
        },
        {
          path: "manage-reservations",
          name: "ManageReservations",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "ManageReservations" */ "../views/Student/ManageReservations"
              ),
          },
          meta: { layout: "global", requiresAuth: true },
        },
        {
          name: "StudentQuizzes",
          path: "student-quizzes",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentQuizzes" */ "../views/Student/StudentQuizzes.vue"
              ),
          },
          beforeEnter: checkLogin,
          meta: { layout: "global", requiresAuth: true },
        },
        {
          path: "student-homework",
          name: "StudentHomeworkList",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentHomework" */ "../components/Dashboard/Homework.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true },
          beforeEnter: checkLogin,
        },
      ],
      meta: { layout: "global", requiresAuth: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/dashboard",
      name: "TutorClasses",
      component: () =>
        import(
          /* webpackChunkName: "StudentClasses" */ "../views/StudentClasses.vue"
        ),
      meta: { layout: "global" },
      children: [
        {
          path: "my-students",
          name: "MyStudents",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "MyStudents" */ "../components/MyStudents/MyStudents.vue"
              ),
          },
          meta: { layout: "tutor", requiresAuth: true },
        },
        {
          path: "payments",
          name: "Payments",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Payments" */ "../components/Payments/Payments.vue"
              ),
          },
          meta: { layout: "tutor", requiresAuth: true },
        },
        {
          path: "my-students/:studentId",
          name: "StudentDetails",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentDetails" */ "../components/MyStudents/StudentDetails.vue"
              ),
          },
          meta: { layout: "tutor", requiresAuth: true },
        },
        {
          path: "my-students/:studentId/360",
          name: "Student360Tutor",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Student360View" */ "../views/Student360View.vue"
              ),
          },
          meta: { layout: "tutor", requiresAuth: true },
          beforeEnter: checkStudent360Access,
        },
        {
          name: "CurrentClasses",
          path: "CurrentClasses",

          components: {
            inner: () =>
              import(
                /* webpackChunkName: "CurrentClasses" */ "../components/TutorClasses/CurrentClasses.vue"
              ),
          },
          meta: { layout: "tutor", requiresAuth: true },
        },
        {
          path: "TutorArchivedClasses",
          name: "TutorArchivedClasses",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "TutorArchivedClasses" */ "../components/TutorClasses/TutorArchivedClasses.vue"
              ),
          },
          meta: { layout: "tutor", requiresAuth: true },
          beforeEnter: checkLogin,
        },
        {
          path: "tutor-homework",
          name: "TutorHomework",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "TutorHomework" */ "../components/Dashboard/Homework.vue"
              ),
          },
          meta: { layout: "tutor", requiresAuth: true },
          beforeEnter: checkLogin,
        },
        {
          name: "TutorCatalogs",
          path: "tutor-catalogs",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Catalog" */ "../components/Dashboard/Catalog.vue"
              ),
          },
          meta: {
            layout: "tutor",
            requiresAuth: true,
            allowed: ["tutor"],
          },
          beforeEnter: authorizeByRoles,
        },

        // HERE TO ADD TUTORqUIZZES INSTEAD OF NAME: QUIZZES.
        {
          path: "quizzes",
          name: "TutorQuizzes",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "QuizView" */ "../views/QuizView.vue"
              ),
          },
        },
      ],
      meta: { layout: "tutor", requiresAuth: true },
      beforeEnter: checkLogin,
    },
    {
      path: "/dashboard",
      name: "TutorsDashboard",
      component: () => import("../views/dashboard.vue"),
      meta: { layout: "global" },
      children: [
        {
          name: "NewTutors",
          path: "new-tutors",

          components: {
            inner: () =>
              import(
                /* webpackChunkName: "NewTutors" */ "../components/Dashboard/NewTutors.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "StudentsGroups",
          path: "students-groups",
          meta: { requiresAuth: true, layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentsGroups" */ "../components/Dashboard/StudentsGroups.vue"
              ),
          },
        },
        {
          name: "StudentGroupsDetails",
          path: "students-groups/:id",
          meta: { requiresAuth: true, layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentGroupsDetails" */ "../views/StudentGroupsDetails.vue"
              ),
          },
        },
        {
          name: "AssignStudents",
          path: "students-groups/:id/assign",
          meta: { requiresAuth: true, layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "AssignStudent" */ "../components/Dashboard/AssignStudent.vue"
              ),
          },
        },
        {
          name: "CoursesManagement",
          path: "courses-management",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "CoursesManagement" */ "../components/Dashboard/CoursesManagement.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "CourseCurriculumMapping",
          path: "course-curriculum-mapping",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "CourseCurriculumMapping" */ "../components/Dashboard/CourseCurriculumMapping.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "PlanRecommendationsList",
          path: "plan-recommendations",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "PlanRecommendationsList" */ "../views/LearningPlan/PlanRecommendationsList.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "PlanRecommendationDetail",
          path: "plan-recommendations/:id",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "PlanRecommendationDetail" */ "../views/LearningPlan/PlanRecommendationDetail.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "GroupClasses",
          path: "group-classes",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "GroupClasses" */ "../components/Dashboard/GroupClasses.vue"
              ),
          },
        },
        {
          name: "Classes",
          path: "classes",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Classes" */ "../components/Dashboard/Classes.vue"
              ),
          },
        },
        {
          name: "studentClasses",
          path: "classes/student/:id",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentClasses" */ "../components/Dashboard/StudentsList/StudentClasses"
              ),
          },
        },
        {
          name: "learningPlanDetail",
          path: "learning-plan/:id",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "LearningPlanDetail" */ "../views/LearningPlan/LearningPlanDetail.vue"
              ),
          },
        },
        {
          name: "createLearningPlan",
          path: "learning-plan/create",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "CreateLearningPlan" */ "../views/LearningPlan/CreateLearningPlan.vue"
              ),
          },
        },
        {
          path: "quizzes",
          name: "Quizzes",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "QuizView" */ "../views/QuizView.vue"
              ),
          },
        },
        {
          path: "quizzes/:name/questions/:id",
          name: "Questions",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "QuestionsView" */ "../views/QuestionsView.vue"
              ),
          },
        },
        {
          name: "AssignStudentGroup",
          path: "group-classes/:id/:ins/assign",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "AssignStudentGroup" */ "../components/Dashboard/AssignStudentGroup.vue"
              ),
          },
        },
        {
          name: "UnderReviewTutors",
          path: "under-review-tutors",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "UnderReviewTutors" */ "../components/Dashboard/UnderReviewTutors.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "ApprovedTutors",
          path: "approved-tutors",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "ApprovedTutors" */ "../components/Dashboard/ApprovedTutors.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "InstituteManagement",
          path: "institute-management",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "InstituteManagement" */ "../views/InstituteManagement.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["admin", "operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "InstituteAdmin",
          path: "institute-admin",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "InstituteAdmin" */ "../views/InstituteAdmin.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "RejectedTutors",
          path: "rejected-tutors",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "RejectedTutors" */ "../components/Dashboard/RejectedTutors.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "TerminatedTutors",
          path: "terminated-tutors",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "TerminatedTutors" */ "../components/Dashboard/TerminatedTutors.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "StudentsList",
          path: "students",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentsListView" */ "../views/StudentsList/StudentsListView.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "Student360",
          path: "students/:id/360",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Student360View" */ "../views/Student360View.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true },
          beforeEnter: checkStudent360Access,
        },
        {
          name: "createLearningPlanForStudent",
          path: "students/:studentId/learning-plan/create",
          meta: { layout: "global" },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "CreateLearningPlan" */ "../views/LearningPlan/CreateLearningPlan.vue"
              ),
          },
        },
        {
          name: "TutorSessionView",
          path: "learning-plan/:planId/session",
          meta: { layout: "global", requiresAuth: true, allowed: ["tutor", "operator", "institute_admin"] },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "TutorSessionView" */ "../views/LearningPlan/TutorSessionView.vue"
              ),
          },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "ParentProgress",
          path: "learning-plan/:planId/progress",
          meta: { layout: "global", requiresAuth: true },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "ParentProgress" */ "../views/LearningPlan/ParentProgress.vue"
              ),
          },
        },
        {
          name: "CourseSetup",
          path: "course-setup/:curriculumId?",
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "institute_admin"] },
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "CourseSetup" */ "../views/LearningPlan/CourseSetup.vue"
              ),
          },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "Homework",
          path: "homework",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Homework" */ "../components/Dashboard/Homework.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "Catalog",
          path: "catalog",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Catalog" */ "../components/Dashboard/Catalog.vue"
              ),
          },
          meta: {
            layout: "global",
            allowed: ["admin", "operator"],
          },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "RescheduleRequests",
          path: "reschedule-requests",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "RescheduleRequests" */ "../components/Dashboard/rescheduleRequests/RescheduleRequests.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "QualityControlDashboard",
          path: "quality-control-dashboard",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "QualityControlDashboard" */ "../views/QualityControlDashboard.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "QualityReports",
          path: "quality-reports",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "QualityReports" */ "../views/QualityReports.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "QualityStandards",
          path: "quality-standards",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "QualityStandards" */ "../views/QualityStandards.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "ClassQualityAudit",
          path: "quality-control/audit/:sessionId",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "ClassQualityAudit" */ "../views/ClassQualityAudit.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "AcceptedRequests",
          path: "accepted-requests",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "AcceptedRequests" */ "../components/Dashboard/rescheduleRequests/AcceptedRequests.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "RefusesRequests",
          path: "refused-requests",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "RefusedRequests" */ "../components/Dashboard/rescheduleRequests/RefusedRequests.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "OperatorWebinars",
          path: "webinars",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "OperatorWebinars" */ "../views/OperatorWebinars.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "ContactUsList",
          path: "contact-us-list",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "ContactUsList" */ "../views/ContactUsList.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "Feedbacks",
          path: "feedbacks",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "Feedbacks" */ "../views/Feedbacks.vue"
              ),
          },
          meta: { layout: "global" },
        },
        {
          name: "StudentAnalyticsDashboardFull",
          path: "student-analytics-dashboard",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentAnalyticsDashboard" */ "../views/StudentAnalyticsDashboard.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        {
          name: "StudentAnalytics",
          path: "student-analytics",
          components: {
            inner: () =>
              import(
                /* webpackChunkName: "StudentAnalyticsDashboard" */ "../views/StudentAnalyticsDashboard.vue"
              ),
          },
          meta: { layout: "global", requiresAuth: true, allowed: ["operator", "super_user", "institute_admin"] },
          beforeEnter: authorizeByRoles,
        },
        // this is for details page layout for each entity eg(quiz, student, homework, courses) ...
        // to continue in coming release
        // {
        //   name: "QuizDetails",
        //   path: "quizzes/:id",
        //   meta: { layout: "global" },
        //   components: {
        //     inner: () =>
        //       import(
        //         /* webpackChunkName: "QuizDetails" */ "../views/DetailsViews/QuizDetails.vue"
        //       ),
        //   },
        // },
        // {
        //   name: "StudentDetails",
        //   path: "students/:id",
        //   meta: { layout: "global" },
        //   components: {
        //     inner: () =>
        //       import(
        //         /* webpackChunkName: "StudentDetails" */ "../views/DetailsViews/StudentDetails.vue"
        //       ),
        //   },
        // },
        // {
        //   name: "HomeworkDetails",
        //   path: "homework/:id",
        //   meta: { layout: "global" },
        //   components: {
        //     inner: () =>
        //       import(
        //         /* webpackChunkName: "HomeworkDetails" */ "../views/DetailsViews/HomeworkDetails.vue"
        //       ),
        //   },
        // },
        // {
        //   name: "CoursesDetails",
        //   path: "courses/:id",
        //   meta: { layout: "global" },
        //   components: {
        //     inner: () =>
        //       import(
        //         /* webpackChunkName: "CoursesDetails" */ "../views/DetailsViews/CoursesDetails.vue"
        //       ),
        //   },
        // },
      ],
    },
    {
      path: "/linkedIn",
      name: "linkedIn",
      component: () =>
        import(/* webpackChunkName: "linkedIn" */ "../views/linkedIn.vue"),
      meta: { layout: "anonymous", guest: true },
    },
    {
      path: "/verifyemail/:id",
      name: "verifyemail",
      component: () =>
        import(
          /* webpackChunkName: "verifyemail" */ "../views/verifyemail.vue"
        ),
      meta: { layout: "anonymous", guest: true },
    },
    {
      path: "/resetpassword/:id",
      name: "resetpassword",
      component: () =>
        import(
          /* webpackChunkName: "resetpassword" */ "../views/resetpassword.vue"
        ),
      meta: { layout: "anonymous", guest: true },
    },
    {
      path: "/About",
      name: "About",
      // route level code-splitting
      // this generates a separate chunk (about.[hash].js) for this route
      // which is lazy-loaded when the route is visited.
      component: () =>
        import(/* webpackChunkName: "about" */ "../views/About.vue"),
      beforeEnter: checkInstituteAccess,
    },
    // {
    //   path: "/course/:id",
    //   name: "CourseTypeDetails",
    //   // route level code-splitting
    //   // this generates a separate chunk (about.[hash].js) for this route
    //   // which is lazy-loaded when the route is visited.
    //   component: () =>
    //     import(
    //       /* webpackChunkName: "about" */ "../views/CourseTypes/CourseTypeDetails.vue"
    //     ),
    // },
    {
      path: "/ContactUs",
      name: "ContactUs",
      component: () =>
        import(/* webpackChunkName: "ContactUs" */ "../views/ContactUs.vue"),
      meta: { layout: "global" },
      beforeEnter: checkLogin,
    },
    //{ path: "*", component: () => import(/* webpackChunkName: "PageNotFound" */ '../views/PageNotFound.vue') }
    {
      //path: "*",
      path: "/:catchAll(.*)",
      name: "404*",
      component: require("../views/error404.vue").default, // load sync home
    },
  ],
  mode: "history",
  //const router = createRouter({
  //    history: createWebHistory(),
  //    routes,
  //}),
})
